import React, { useEffect, useState } from "react";
import { Store as StoreIcon, ExternalLink, ImageOff } from "lucide-react";
import { api } from "@/lib/api";

/**
 * MerchShowcase — public product strip rendered on a streamer's home page.
 * Each card links directly to the streamer's external storefront. MystiCast
 * neither processes payment nor fulfills orders.
 */
export default function MerchShowcase({ channel }) {
  const [items, setItems] = useState([]);
  const [shopUrl, setShopUrl] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!channel?.username) return;
    api.get(`/channels/${channel.username}/merch`)
      .then((r) => { setItems(r.data.items || []); setShopUrl(r.data.shop_url || null); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [channel?.username]);

  if (loading) return null;
  if (items.length === 0 && !shopUrl) return null;

  return (
    <div className="sc-card p-5" data-testid="merch-showcase">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <StoreIcon className="w-4 h-4 text-[color:var(--sc-pink)]" />
          <div className="sc-label" style={{ color: "var(--sc-pink)" }}>◤ MERCH</div>
          <div className="sc-heading text-lg font-black">{channel.username}&apos;s shop</div>
        </div>
        {shopUrl && (
          <a
            href={shopUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="sc-btn-ghost text-xs flex items-center gap-1.5"
            data-testid="merch-visit-shop"
          >
            Visit full shop <ExternalLink className="w-3 h-3" />
          </a>
        )}
      </div>

      {items.length === 0 ? (
        <div className="text-xs text-zinc-500 text-center py-6">Storefront available — click above to browse.</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {items.map((it) => (
            <a
              key={it.id}
              href={it.external_url}
              target="_blank"
              rel="noopener noreferrer"
              className="group block border border-white/10 hover:border-[color:var(--sc-pink)]/50 rounded overflow-hidden transition"
              data-testid={`merch-item-${it.id}`}
            >
              <div className="aspect-square bg-black/30 grid place-items-center overflow-hidden">
                {it.image_url ? (
                  <img src={it.image_url} alt={it.title} className="w-full h-full object-cover group-hover:scale-105 transition duration-500" />
                ) : (
                  <ImageOff className="w-6 h-6 text-zinc-700" />
                )}
              </div>
              <div className="p-2.5">
                <div className="sc-heading text-xs font-bold truncate group-hover:text-[color:var(--sc-pink)]">{it.title}</div>
                {it.price_display && (
                  <div className="sc-mono text-[10px] text-zinc-400 mt-0.5">{it.price_display} {it.currency}</div>
                )}
              </div>
            </a>
          ))}
        </div>
      )}

      <div className="mt-3 text-[9px] sc-mono text-zinc-600 text-center">
        Merch is sold directly by {channel.username}. MystiCast is not the seller.
      </div>
    </div>
  );
}
